import {
  EmailAuthProvider,
  GoogleAuthProvider,
  getAuth,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import app from '../firebase';
import { reserveUsername } from './firestore';

export const auth = getAuth(app);

// Thrown by linkGoogleAccount()/linkEmailAccount() instead of silently
// recovering when the credential belongs to a pre-existing account. Callers
// must show explicit confirmation UI (see AuthModal's conflict state) and
// only call resolveAuthConflict() once the user opts in — linking into the
// wrong account silently would abandon the guest's current uid and
// everything tied to it with no warning, which is the exact bug this type
// exists to prevent.
export class AuthConflictError extends Error {
  constructor({ method, email, credential }) {
    super('An account with these credentials already exists.');
    this.name = 'AuthConflictError';
    this.method = method; // 'google' | 'password'
    this.email = email; // best-effort, may be undefined for 'google'
    this.credential = credential; // AuthCredential — reusable directly with signInWithCredential
  }
}

// Resolves once we have a signed-in user (anonymous or otherwise), signing
// in anonymously if nobody's signed in yet. Used right before any write that
// needs a stable uid, so it doesn't race the sign-in that also happens
// reactively via useAuthUser.
export function ensureSignedIn() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        if (user) {
          resolve(user);
        } else {
          signInAnonymously(auth).then((cred) => resolve(cred.user), reject);
        }
      },
      reject
    );
  });
}

// Upgrades the current anonymous session to a real Google account without
// changing its uid, so every playerProfiles doc keyed on that uid carries
// straight over. If this Google account is already linked to a *different*
// anonymous session (e.g. signing in for the first time on a new device),
// Firebase refuses the link — that's surfaced as an AuthConflictError rather
// than silently recovered, since silently switching to the other account
// would abandon this session's guest data with no warning.
export async function linkGoogleAccount() {
  const provider = new GoogleAuthProvider();
  try {
    const result = await linkWithPopup(auth.currentUser, provider);
    return result.user;
  } catch (err) {
    if (err.code === 'auth/credential-already-in-use') {
      const credential = GoogleAuthProvider.credentialFromError(err);
      throw new AuthConflictError({ method: 'google', email: err.customData?.email, credential });
    }
    throw err;
  }
}

// Upgrades the current anonymous session to a real email/password account,
// same shape as linkGoogleAccount() above: same uid, so every playerProfiles
// doc keyed on it carries straight over. Also reserves `username` (see
// reserveUsername) and sets it as this user's Auth displayName, which is
// what lets the Cloud Function prefer a durable reserved name over
// whatever free-text name got typed into a given tournament's lobby —
// deliberately not written to playerProfiles directly, since that
// collection stays Admin-SDK-write-only (see
// docs/auth-leaderboard-plan.md).
//
// If this email is already registered (e.g. this person registered from a
// different anonymous session on another device), Firebase refuses the
// link — surfaced as an AuthConflictError, same reasoning as
// linkGoogleAccount above.
export async function linkEmailAccount(email, password, username) {
  const credential = EmailAuthProvider.credential(email, password);
  try {
    const result = await linkWithCredential(auth.currentUser, credential);
    await reserveUsername(result.user.uid, username);
    await updateProfile(result.user, { displayName: username });
    return result.user;
  } catch (err) {
    if (err.code === 'auth/email-already-in-use' || err.code === 'auth/credential-already-in-use') {
      throw new AuthConflictError({ method: 'password', email, credential });
    }
    throw err;
  }
}

// Only called after explicit user confirmation of an AuthConflictError (see
// AuthModal) — switches auth.currentUser to the pre-existing account,
// abandoning the guest session's uid. The UI must have already warned about
// that data loss before this is called; this function performs it, it
// doesn't decide whether to.
//
// EmailAuthProvider.credential(email, password) works with
// signInWithCredential exactly like it does with signInWithEmailAndPassword,
// which is what lets both the Google and password conflict paths resolve
// through this one function instead of two.
export async function resolveAuthConflict(conflict) {
  try {
    const result = await signInWithCredential(auth, conflict.credential);
    return result.user;
  } catch (err) {
    if (
      conflict.method === 'password' &&
      ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found'].includes(err.code)
    ) {
      // The email conflict was real, but the password just typed doesn't
      // match that account — most likely because it was created via Google
      // instead of a password, so there's nothing for signInWithCredential
      // to check the password against.
      throw new Error(
        'That password doesn\'t match this email\'s existing account, or it was created with Google instead. Try "Sign in with Google," or double-check your password.'
      );
    }
    throw err;
  }
}

// Plain sign-in for a user who already registered (on this device or
// another one) and is currently anonymous again — e.g. a new browser
// profile, or cookies cleared. Not an upgrade-in-place like the functions
// above: this switches auth.currentUser to the pre-existing registered
// account's uid, abandoning whatever anonymous uid was active before the
// call (its playerProfiles doc, if any, is simply left behind).
export async function signInWithEmail(email, password) {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function sendPasswordReset(email) {
  await sendPasswordResetEmail(auth, email);
}
